/**
 * WaitlistDialog — shown when a Google sign-in is VERIFIED but the account is
 * not on the console allowlist (auth_users.console = false / absent). Console
 * access is DB-gated, so everyone else lands here with a clear path to request
 * access instead of a bare error string.
 */

import { useEffect } from "react";
import { Icons } from "./icons";

/** Who to contact for access (per product: quick-access owner). */
const CONTACT_EMAIL = "aamir@thema.financial";

export function WaitlistDialog({
  email,
  onClose,
  onTryAnother,
}: {
  /** The verified Google account that isn't allowlisted (shown to the user). */
  email: string;
  onClose: () => void;
  onTryAnother: () => void;
}) {
  // Dismiss on Escape, matching native dialog affordances.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mailto =
    `mailto:${CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent("Thema Console — access request")}` +
    `&body=${encodeURIComponent(
      `Hi Aamir,\n\nI'd like access to the Thema Console.\nMy Google account: ${email}\n\nThanks!`,
    )}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-title"
      onMouseDown={(e) => {
        // Click on the backdrop (not the card) closes.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "rgba(14,23,38,0.40)",
        backdropFilter: "blur(8px) saturate(1.2)",
        WebkitBackdropFilter: "blur(8px) saturate(1.2)",
        animation: "fadeIn 0.18s var(--smooth)",
      }}
    >
      <div
        className="glass"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 444,
          borderRadius: 18,
          padding: "32px 30px 28px",
          boxShadow: "var(--card-shadow)",
          animation: "scaleIn 0.22s var(--smooth)",
        }}
      >
        {/* close */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="btn btn-ghost"
          style={{ position: "absolute", top: 14, right: 14, width: 34, height: 34, minWidth: 0, padding: 0, borderRadius: 9 }}
        >
          <Icons.close size={16} />
        </button>

        {/* icon badge */}
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            display: "grid",
            placeItems: "center",
            background: "var(--accent-soft)",
            color: "var(--accent)",
            marginBottom: 18,
          }}
        >
          <Icons.lock size={24} />
        </div>

        <div className="eyebrow" style={{ marginBottom: 10 }}>Access pending</div>
        <h2
          id="waitlist-title"
          className="serif"
          style={{ fontSize: 25, fontWeight: 700, letterSpacing: "-0.01em", margin: 0 }}
        >
          You're on the waitlist
        </h2>

        <p style={{ fontSize: 14.5, lineHeight: 1.62, color: "var(--text-soft)", margin: "12px 0 0" }}>
          Your Google account{" "}
          <span className="mono" style={{ color: "var(--text)", fontWeight: 600, wordBreak: "break-all" }}>
            {email}
          </span>{" "}
          isn't enabled for the Thema Console yet — access is granted per account.
        </p>
        <p style={{ fontSize: 14.5, lineHeight: 1.62, color: "var(--text-soft)", margin: "12px 0 24px" }}>
          For quick access, please connect with{" "}
          <a className="link" href={mailto} style={{ fontWeight: 600 }}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>

        <a
          className="btn btn-primary"
          href={mailto}
          style={{ width: "100%", height: 48, textDecoration: "none" }}
        >
          <Icons.ext size={16} /> Email {CONTACT_EMAIL}
        </a>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onTryAnother}
          style={{ width: "100%", height: 44, marginTop: 10 }}
        >
          Use a different account
        </button>
      </div>
    </div>
  );
}
